import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import axios from "axios";
import HeaderMain from "./Components/HeaderMain.jsx";
import TextAds from "../components/textAd.jsx";
import ImageVideoAds from "../components/imageVideoAd.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faInfo,
  faArrowLeft,
  faEye,
  faArrowRight,
  faArrowCircleRight,
  faArrowRightFromBracket,
  faAngleRight,
  faQuoteLeft,
  faQuoteRight,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./AllNews.module.css";
import { useAuth } from "../context/AuthContext";

const AllNews = ({ preview, selectTheSlot }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [newsItems, setNewsItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [viewMode, setViewMode] = useState("all");
  const [userInterests, setUserInterests] = useState([]);
  const [expandedNews, setExpandedNews] = useState(null);
  const [hasPersonalization, setHasPersonalization] = useState(false);
  const [carouselSlides, setCarouselSlides] = useState({}); // Track slide index for each news item

  const { user } = useAuth();
  const userId = user?.uid;
  const [adsSelected, setAdsSelected] = useState([]);
  const [currentAdIndexes, setCurrentAdIndexes] = useState({
    0: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: 0,
  });
  const [selectedAd, setSelectedAd] = useState(null);
  const [ads, setAds] = useState([]);

  useEffect(() => {
    if (user?.userInterests && Array.isArray(user.userInterests)) {
      setUserInterests(user.userInterests);
      setHasPersonalization(user.userInterests.length > 0);
      if (user.userInterests.length > 0 && viewMode === "all") {
        setViewMode("personalized");
      }
    } else {
      setUserInterests([]);
      setHasPersonalization(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNews();
  }, [viewMode, selectedDate]);

  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (
  //       isCalendarVisible &&
  //       !event.target.closest(`.${styles["calendar-control"]}`)
  //     ) {
  //       setIsCalendarVisible(false);
  //     }
  //   };

  //   document.addEventListener("mousedown", handleClickOutside);
  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside);
  //   };
  // }, [isCalendarVisible]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      let url;
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const dateParam = `${year}-${month}-${day}`;

      if (viewMode === "personalized" && (userId || userInterests.length > 0)) {
        // For personalized news, fetch all news and filter on client side
        url = `${process.env.REACT_APP_BASE_URL}/rss/api/all-news?selectedDate=${dateParam}`;
      } else if (viewMode === "top") {
        url = `${process.env.REACT_APP_BASE_URL}/rss/api/top-news?selectedDate=${dateParam}&limit=12`;
      } else {
        url = `${process.env.REACT_APP_BASE_URL}/rss/api/all-news?selectedDate=${dateParam}`;
      }

      const response = await axios.get(url);

      if (response.data.success) {
        let fetchedNews = response.data.news;

        // Process admin news items to fetch their images
        const processedNews = await Promise.all(
          fetchedNews.map(async (newsItem) => {
            if (
              newsItem.source_type === "admin" &&
              newsItem.images === "images_available"
            ) {
              try {
                // Fetch full admin news details to get images
                const adminNewsResponse = await axios.get(
                  `${process.env.REACT_APP_BASE_URL}/admin-news/news/${newsItem.id}`
                );
                return {
                  ...newsItem,
                  images: adminNewsResponse.data.images || [],
                  video_path:
                    adminNewsResponse.data.video_path || newsItem.video_path,
                };
              } catch (error) {
                console.error("Error fetching admin news details:", error);
                return { ...newsItem, images: [] };
              }
            }
            return newsItem;
          })
        );

        // Apply client-side filtering for personalized view
        let finalNews = processedNews;
        if (viewMode === "personalized" && userInterests.length > 0) {
          const userSportIds = userInterests.map((interest) => interest.id); // Use .id not .sport_id
          console.log("User sport IDs from localStorage:", userSportIds);
          console.log("Available news count:", processedNews.length);

          finalNews = processedNews.filter((newsItem) => {
            // Include news that matches user's sports interests
            const matches = userSportIds.includes(newsItem.sport_id);
            console.log(
              `News "${newsItem.title}" sport_id: ${newsItem.sport_id}, matches: ${matches}`
            );
            return matches;
          });

          console.log(
            `Filtered to ${finalNews.length} personalized news items`
          );
        }

        setNewsItems(finalNews);

        if (response.data.userInterests) {
          setUserInterests(response.data.userInterests);
        }
        if (response.data.hasPersonalization !== undefined) {
          setHasPersonalization(response.data.hasPersonalization);
        }
      }
    } catch (error) {
      console.error("Error fetching news:", error);
      setNewsItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewsClick = async (newsItem) => {
    if (handleNewsClick.processing) return;
    handleNewsClick.processing = true;

    try {
      if (newsItem.source_type === "admin") {
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}/admin-news/news/increment-click/${newsItem.id}`
        );
        window.open(`/display-user-news/${newsItem.id}`, "_blank");
      } else {
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}/rss/api/increment-click/${newsItem.id}`
        );
        window.open(newsItem.link, "_blank");
      }
    } catch (error) {
      console.error("Error tracking click:", error);
      if (newsItem.source_type === "admin") {
        window.open(`/display-user-news/${newsItem.id}`, "_blank");
      } else {
        window.open(newsItem.link, "_blank");
      }
    } finally {
      setTimeout(() => {
        handleNewsClick.processing = false;
      }, 1000);
    }
  };

  const toggleNewsExpanded = (newsId) => {
    setExpandedNews(expandedNews === newsId ? null : newsId);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getSportNames = () => {
    if (!hasPersonalization) return "all sports";
    if (!Array.isArray(userInterests) || userInterests.length === 0)
      return "all sports";
    const sportNames = userInterests.map((i) => i.name).join(", ");
    return sportNames || "your interested sports";
  };

  const stripMarkdownAndTruncate = (content, maxLength = 200) => {
    if (!content) return "";

    const stripped = content
      .replace(/#{1,6}\s+/g, "")
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/`(.*?)`/g, "$1")
      .replace(/\[(.*?)\]\(.*?\)/g, "$1")
      .replace(/!\[.*?\]\(.*?\)/g, "")
      .replace(/\n+/g, " ")
      .trim();

    return stripped.length > maxLength
      ? stripped.substring(0, maxLength) + "..."
      : stripped;
  };

  const handleSlideChange = (newsId, index) => {
    setCarouselSlides((prev) => ({ ...prev, [newsId]: index }));
  };

  const getMediaItems = (newsItem) => {
    const media = [];
    if (newsItem.images && newsItem.images.length > 0) {
      media.push(
        ...newsItem.images.map((img, index) => ({
          type: "image",
          src: typeof img === "string" ? img : `data:image/jpeg;base64,${img}`,
          alt: `${newsItem.title} ${index + 1}`,
        }))
      );
    }
    if (newsItem.video_path) {
      // Handle admin video paths
      const videoSrc =
        newsItem.source_type === "admin"
          ? `${process.env.REACT_APP_BASE_URL}${newsItem.video_path}`
          : newsItem.video_path;
      media.push({ type: "video", src: videoSrc });
    }
    return media;
  };

  useEffect(() => {
    if (preview) return;

    let isMounted = true;
    let timeoutId;

    const fetchAds = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/download-ad`
        );
        if (isMounted) setAds(response.data.ads);
      } catch (error) {
        console.error("Error fetching ads:", error);
      }

      if (isMounted) {
        timeoutId = setTimeout(fetchAds, 500);
      }
    };

    fetchAds();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [preview]);

  const handleClicks = async (e, ad) => {
    if (preview) {
      const id = e.currentTarget.dataset.id;

      if (
        (id === "ad-1" && preview === "text") ||
        ((id === "ad-2" || id === "ad-3") && preview === "image") ||
        ((id === "ad-4" || id === "ad-5") && preview === "video")
      ) {
        setSelectedAd(id);
        selectTheSlot(id);
      }
    }

    e.preventDefault();

    if (!ad || !ad.url) return;
    window.location.href = ad.url;
    ad.clicks = ad.clicks + 1;

    try {
      const jsonData = {
        clicks: ad.clicks,
        id: ad.ids,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/upload-clicks`,
        jsonData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.success) {
      } else {
        throw new Error(response.data.message || "Failed to upload clicks");
      }
    } catch (error) {
      console.error("Error uploading clicks:", error);
      alert("Failed to upload Clicks: " + error.message);
    }
  };

  const convertDate = (dateString) => {
    const dateObj = new Date(dateString);

    dateObj.setDate(dateObj.getDate());

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");

    const formattedDate = `${year}-${month}-${day}`;
    return formattedDate;
  };

  useEffect(() => {
    const getCurrentAds = () => {
      const now = new Date();
      const isoDate = now.toISOString().slice(0, 10); // "2025-06-17"
      const current_date = `${isoDate}T00:00:00`;
      const current_time = now.toTimeString().split(" ")[0];

      const newAds = [];

      for (const ad of ads) {
        const isDateInRange =
          ad.start_date.split("T")[0] <= current_date.split("T")[0] &&
          convertDate(current_date) <= ad.end_date.split("T")[0];

        const datePart = convertDate(current_date);
        const currentTime = new Date(`${datePart}T${current_time}`);
        const startTime = new Date(`${datePart}T${ad.start_time}`);
        const endTime = new Date(`${datePart}T${ad.end_time}`);
        const isTimeInRange =
          startTime <= currentTime && currentTime <= endTime;

        const isTimeIterRemaining = ad.total_time > 0;

        if (
          isDateInRange &&
          isTimeInRange &&
          isTimeIterRemaining &&
          ad.slotPlace.split(" ")[0] === "newsHomePage"
        ) {
          newAds.push(ad);
        }
      }

      if (JSON.stringify(adsSelected) !== JSON.stringify(newAds)) {
        const indexedAds = [];

        newAds.forEach((ad) => {
          const slotPlace = ad.slotPlace;
          const index = parseInt(slotPlace.trim().split(" ").pop(), 10);

          if (!isNaN(index)) {
            if (!indexedAds[index]) {
              indexedAds[index] = [];
            }
            indexedAds[index].push(ad);
          }
        });

        setAdsSelected(indexedAds);
      }
    };

    getCurrentAds();

    const interval = setInterval(() => {
      getCurrentAds();
    }, 1000);

    return () => clearInterval(interval);
  }, [ads]);

  const updateIterations = async (slot, i) => {
    if (!adsSelected[slot] || !adsSelected[slot][i]) return;

    try {
      const jsonData = {
        iterations: adsSelected[slot][i].iterations,
        total_time: adsSelected[slot][i].total_time,
        id: adsSelected[slot][i].ids,
        intervals: adsSelected[slot][i].intervals,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/upload-iterations`,
        jsonData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.success) {
      } else {
        throw new Error(response.data.message || "Failed to upload iterations");
      }
    } catch (error) {
      console.error("Error uploading iterations:", error);
      alert("Failed to upload Iterations: " + error.message);
    }
  };

  useEffect(() => {
    const timers = {};

    Object.keys(adsSelected).forEach((slotKey) => {
      const slot = parseInt(slotKey, 10);
      const adsInSlot = adsSelected[slot];
      const currentIndex = currentAdIndexes[slot];

      if (!adsInSlot || adsInSlot.length === 0 || currentIndex < 0) return;

      let nextIndex = currentIndex;

      const updateSlot = () => {
        // Update iterations if current ad is valid
        if (
          currentIndex < adsInSlot.length &&
          adsInSlot[currentIndex]?.iterations > 0
        ) {
          updateIterations(slot, currentIndex);
        }

        // Find next valid index with iterations > 0
        nextIndex = (currentIndex + 1) % adsInSlot.length;

        if (adsInSlot[nextIndex]?.iterations <= 0) {
          const originalIndex = nextIndex;
          while (
            adsInSlot[nextIndex]?.iterations <= 0 &&
            nextIndex !== currentIndex
          ) {
            nextIndex = (nextIndex + 1) % adsInSlot.length;
            if (nextIndex === originalIndex) break;
          }
        }

        // Prevent infinite loop if no valid ad is found
        if (adsInSlot[nextIndex]?.iterations <= 0) return;

        // Update index state
        setCurrentAdIndexes((prev) => ({
          ...prev,
          [slot]: nextIndex,
        }));
      };

      const interval =
        adsInSlot[currentIndex]?.iterations > 0
          ? adsInSlot[currentIndex]?.intervals
          : 1000;

      timers[slot] = setTimeout(updateSlot, interval);
    });

    return () => {
      // Clear all timers on cleanup
      Object.values(timers).forEach(clearTimeout);
    };
  }, [
    JSON.stringify(currentAdIndexes),
    JSON.stringify(
      Object.fromEntries(
        Object.entries(adsSelected).map(([slot, ads]) => [
          slot,
          ads.map((ad) => ad.iterations).join(","),
        ])
      )
    ),
  ]);

  return (
    <div className={styles["all-news-container"]}>
      <HeaderMain pageType="all-news" />
      <div className={styles["ads-container"]}>
        {/* Text Ad Section */}
        <TextAds
          adsSelected={adsSelected}
          currentAdIndexes={currentAdIndexes}
          selectedAd={selectedAd}
          handleClicks={handleClicks}
          preview={preview}
          slot={1}
        />

        <div className={styles["main-container"]}>
          <div className={styles["all-news-content"]}>
            <div className={styles["news-header"]}>
              <h3 className={styles["page-title"]}>Sports News</h3>

              <div className={styles["news-controls"]}>
                <div className={styles["view-mode-controls"]}>
                  {(userId && hasPersonalization) ||
                  userInterests.length > 0 ? (
                    <button
                      onClick={() => setViewMode("personalized")}
                      className={`${styles["mode-button"]} ${
                        viewMode === "personalized" ? styles.active : ""
                      }`}
                    >
                      For You
                    </button>
                  ) : null}

                  <button
                    onClick={() => setViewMode("top")}
                    className={`${styles["mode-button"]} ${
                      viewMode === "top" ? styles.active : ""
                    }`}
                  >
                    Trending
                  </button>

                  <button
                    onClick={() => setViewMode("all")}
                    className={`${styles["mode-button"]} ${
                      viewMode === "all" ? styles.active : ""
                    }`}
                  >
                    All Sports
                  </button>
                </div>
                <div className={styles["calendar-control"]}>
                  <button
                    onClick={() => setIsCalendarVisible(!isCalendarVisible)}
                    className={styles["calendar-button"]}
                  >
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    Filter by Date
                  </button>
                  {isCalendarVisible && (
                    <div className={styles["calendar-dropdown"]}>
                      <Calendar
                        onChange={setSelectedDate}
                        value={selectedDate}
                        className={styles.calendar}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {!loading && (
              <div className={styles["news-info"]}>
                {viewMode === "personalized" &&
                  (hasPersonalization || userInterests.length > 0) && (
                    <p className={styles["filter-info"]}>
                      {newsItems.length === 0
                        ? `No news available for ${getSportNames()} on ${formatDate(
                            selectedDate
                          )}`
                        : `Showing ${
                            newsItems.length
                          } news articles for ${getSportNames()} on ${formatDate(
                            selectedDate
                          )}`}
                    </p>
                  )}
                {viewMode === "top" && (
                  <p className={styles["filter-info"]}>
                    {newsItems.length === 0
                      ? `No trending news available for ${formatDate(
                          selectedDate
                        )}`
                      : `Showing ${
                          newsItems.length
                        } trending news articles for ${formatDate(
                          selectedDate
                        )}`}
                  </p>
                )}
                {viewMode === "all" && (
                  <p className={styles["filter-info"]}>
                    {newsItems.length === 0
                      ? `No news available for ${formatDate(selectedDate)}`
                      : `Showing ${
                          newsItems.length
                        } news articles for all sports on ${formatDate(
                          selectedDate
                        )}`}
                  </p>
                )}
              </div>
            )}

            {loading ? (
              <div className={styles.loading}>Loading news...</div>
            ) : (
              <div className={styles["news-grid"]}>
                {newsItems.length === 0 ? (
                  <div className={styles["no-news"]}>
                    <h3>No news found</h3>
                    <p>Try selecting a different date or view mode.</p>
                  </div>
                ) : (
                  newsItems.map((newsItem) => {
                    const mediaItems = getMediaItems(newsItem);

                    return (
                      <div
                        key={`${newsItem.source_type}-${newsItem.id}`}
                        className={styles["news-card"]}
                      >
                        <div className={styles["news-card-header"]}>
                          <div>
                            {" "}
                            <span
                              className={`${styles["source-badge"]} ${
                                styles[newsItem.source_type]
                              }`}
                            >
                              {newsItem.source_type === "admin"
                                ? "Official"
                                : "RSS"}
                            </span>
                            {newsItem.sport_name && (
                              <span className={styles["sport-badge"]}>
                                {newsItem.sport_name}
                              </span>
                            )}
                          </div>
                        </div>
                        {mediaItems.length > 0 && (
                          <div className={styles["news-carousel"]}>
                            <Carousel
                              showThumbs={false}
                              autoPlay={false}
                              showIndicators={false}
                              infiniteLoop={true}
                              interval={5000}
                              stopOnHover={true}
                              showStatus={false}
                              showArrows={false}
                              selectedItem={carouselSlides[newsItem.id] || 0}
                              onChange={(index) =>
                                handleSlideChange(newsItem.id, index)
                              }
                            >
                              {mediaItems.map((media, index) => (
                                <div
                                  key={index}
                                  className={styles["media-container"]}
                                  onClick={() => handleNewsClick(newsItem)}
                                >
                                  {media.type === "image" ? (
                                    <img
                                      src={media.src}
                                      alt={
                                        media.alt ||
                                        `${newsItem.title} ${index + 1}`
                                      }
                                      className={styles["news-media"]}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  ) : (
                                    <video
                                      src={media.src}
                                      className={styles["news-video"]}
                                      onClick={(e) => e.stopPropagation()}
                                      autoPlay
                                      muted
                                      loop
                                      playsInline
                                    />
                                  )}
                                </div>
                              ))}
                            </Carousel>
                            <div className={styles["custom-control-dots"]}>
                              {mediaItems.map((_, index) => (
                                <span
                                  key={index}
                                  className={`${styles["custom-dot"]} ${
                                    (carouselSlides[newsItem.id] ?? 0) === index
                                      ? styles.active
                                      : ""
                                  }`}
                                  onClick={() =>
                                    handleSlideChange(newsItem.id, index)
                                  }
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        <div className={styles["news-card-lower"]}>
                          <div className={styles["news-card-meta"]}>
                            <span className={styles["news-date"]}>
                              {formatDate(newsItem.date)} &nbsp;|&nbsp;
                              <span className={styles["news-clicks"]}>
                                <FontAwesomeIcon icon={faEye} />{" "}
                                {newsItem.clicks || 0}
                              </span>
                            </span>
                            {/* <button
                          onClick={() => toggleNewsExpanded(newsItem.id)}
                          className={styles["expand-button"]}
                        >
                          <FontAwesomeIcon icon={faInfo} />
                        </button> */}
                          </div>
                          <h3
                            className={styles["news-card-title"]}
                            onClick={() => handleNewsClick(newsItem)}
                          >
                            {newsItem.title}
                          </h3>

                          {expandedNews === newsItem.id && (
                            <div className={styles["news-card-content"]}>
                              <p>
                                {stripMarkdownAndTruncate(newsItem.content)}
                              </p>
                            </div>
                          )}

                          <button
                            onClick={() => handleNewsClick(newsItem)}
                            className={styles["read-more-button"]}
                          >
                            {newsItem.source_type === "admin"
                              ? "Read Full Article"
                              : "View Original"}
                            <FontAwesomeIcon icon={faAngleRight} />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
          <ImageVideoAds
            adsSelected={adsSelected}
            currentAdIndexes={currentAdIndexes}
            preview={preview}
            handleClicks={handleClicks}
            selectedAd={selectedAd}
            slot1={2}
            slot2={4}
          />
        </div>
      </div>
    </div>
  );
};

export default AllNews;
